/**
 * Demo posts data for the home feed
 * Contains 45 posts with various types to test the feed functionality
 */

import type { ExtendedPost } from '../types/index';

// Sample media items for reference - not used directly but shows structure

// Sample song items for reference - not used directly but shows structure

// Generate 45 demo posts with different types
export const demoPosts: ExtendedPost[] = [
  // Image posts (15)
  ...Array(15).fill(null).map((_, index) => ({
    id: `image-post-${index + 1}`,
    user_id: `user-${index + 1}`,
    content_id: `content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/photo/2018/01/14/23/12/nature-3082832_1280.jpg`,
    caption: `Beautiful scenery from my recent trip! #nature #photography #travel ${index + 1}`,
    likes: Math.floor(Math.random() * 1000) + 50,
    views: Math.floor(Math.random() * 5000) + 200,
    type: 'image' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * (index + 1)).toISOString(),
    location: index % 3 === 0 ? `City ${index + 1}, Country ${index % 5 + 1}` : undefined,
    users: {
      id: `user_${index + 1}_author`,
      username: `nature_lover_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'women' : 'men'}/${index % 10 + 1}.jpg`,
      is_verified: index % 5 === 0,
      verified: index % 5 === 0,
      location: index % 3 === 0 ? `City ${index + 1}, Country ${index % 5 + 1}` : undefined,
      is_followed: index % 4 === 0,
    },
    comments: [],
    mediaItems: [
      { 
        id: `image-${index + 1}`, 
        url: `https://cdn.pixabay.com/photo/201${8 - (index % 5)}/0${(index % 9) + 1}/1${(index % 9) + 1}/nature-${3082832 + index}_1280.jpg`, 
        type: 'image' as const 
      }
    ],
  })),

  // Video posts (10)
  ...Array(10).fill(null).map((_, index) => ({
    id: `video-post-${index + 1}`,
    user_id: `video-user-${index + 1}`,
    content_id: `video-content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/vimeo/330534929/lake-24543.mp4`,
    caption: `Check out this amazing video! #video #creative #film ${index + 1}`,
    likes: Math.floor(Math.random() * 800) + 100,
    views: Math.floor(Math.random() * 10000) + 500,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * (index + 1)).toISOString(),
    location: index % 4 === 0 ? `VideoCity ${index + 1}, VideoLand` : undefined,
    users: {
      id: `video_user_${index + 1}_author`,
      username: `video_creator_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'men' : 'women'}/${10 + (index % 10)}.jpg`,
      is_verified: index % 3 === 0,
      verified: index % 3 === 0,
      location: index % 4 === 0 ? `VideoCity ${index + 1}, VideoLand` : undefined,
      is_followed: index % 5 === 0,
    },
    comments: [],
    mediaItems: [
      { 
        id: `video-${index + 1}`, 
        url: `https://cdn.pixabay.com/vimeo/${330534929 + (index * 1000)}/video-${24543 + index}.mp4`, 
        type: 'video' as const,
        thumbnail: `https://cdn.pixabay.com/photo/201${7 - (index % 3)}/0${(index % 9) + 1}/2${(index % 9) + 1}/landscape-${2925913 + index}_1280.jpg`
      }
    ],
  })),

  // Music posts (10)
  ...Array(10).fill(null).map((_, index) => ({
    id: `music-post-${index + 1}`,
    user_id: `music-user-${index + 1}`,
    content_id: `music-content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/photo/2015/05/07/11/02/guitar-756326_1280.jpg`,
    caption: `My latest track is out now! Give it a listen 🎵 #music #newrelease #artist ${index + 1}`,
    likes: Math.floor(Math.random() * 600) + 150,
    views: Math.floor(Math.random() * 8000) + 300,
    type: 'music' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 36 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 36 * (index + 1)).toISOString(),
    location: index % 5 === 0 ? `Music Studio ${index + 1}, Harmony City` : undefined,
    users: {
      id: `music_user_${index + 1}_author`,
      username: `music_artist_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'women' : 'men'}/${20 + (index % 10)}.jpg`,
      is_verified: index % 2 === 0,
      verified: index % 2 === 0,
      location: index % 5 === 0 ? `Music Studio ${index + 1}, Harmony City` : undefined,
      is_followed: index % 3 === 0,
    },
    comments: [],
    songItems: [
      { 
        id: `music-${index + 1}`, 
        title: `Track ${index + 1} - ${['Summer Vibes', 'Midnight Dreams', 'Urban Rhythm', 'Ocean Waves', 'Mountain Echo'][index % 5]}`,
        artist: `Artist ${index + 1} - ${['Chill Wave', 'Luna Echo', 'City Beats', 'Nature Sounds', 'Mountain Melody'][index % 5]}`,
        url: `https://cdn.pixabay.com/audio/202${index % 3}/0${(index % 9) + 1}/audio_${Math.floor(Math.random() * 1000000)}.mp3`,
        cover_image: `https://cdn.pixabay.com/photo/201${6 - (index % 3)}/0${(index % 9) + 1}/1${(index % 9) + 1}/music-${756326 + index}_1280.jpg`,
        duration: `${Math.floor(Math.random() * 4) + 2}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`
      }
    ],
  })),

  // Multi-media posts (5)
  ...Array(5).fill(null).map((_, index) => ({
    id: `multi-media-post-${index + 1}`,
    user_id: `multi-media-user-${index + 1}`,
    content_id: `multi-media-content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/photo/2015/03/26/09/47/sky-690293_1280.jpg`,
    caption: `A collection of my favorite moments from this week! #memories #collection #moments ${index + 1}`,
    likes: Math.floor(Math.random() * 1200) + 200,
    views: Math.floor(Math.random() * 15000) + 1000,
    type: 'multi-media' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 48 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 48 * (index + 1)).toISOString(),
    location: index % 2 === 0 ? `Gallery ${index + 1}, Art City` : undefined,
    users: {
      id: `multi_media_user_${index + 1}_author`,
      username: `gallery_creator_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'men' : 'women'}/${30 + (index % 10)}.jpg`,
      is_verified: index % 2 === 1,
      verified: index % 2 === 1,
      location: index % 2 === 0 ? `Gallery ${index + 1}, Art City` : undefined,
      is_followed: index % 3 === 1,
    },
    comments: [],
    mediaItems: [
      { id: `multi-media-${index + 1}-1`, url: `https://cdn.pixabay.com/photo/201${5 - (index % 3)}/0${(index % 9) + 1}/2${(index % 9) + 1}/nature-${690293 + index * 10}_1280.jpg`, type: 'image' as const },
      { id: `multi-media-${index + 1}-2`, url: `https://cdn.pixabay.com/photo/201${6 - (index % 3)}/0${(index % 9) + 2}/1${(index % 9) + 2}/landscape-${1072828 + index * 20}_1280.jpg`, type: 'image' as const },
      { id: `multi-media-${index + 1}-3`, url: `https://cdn.pixabay.com/vimeo/${328370995 + index * 1000}/nature-${24457 + index}.mp4`, type: 'video' as const, thumbnail: `https://cdn.pixabay.com/photo/201${7 - (index % 3)}/0${(index % 9) + 3}/0${(index % 9) + 1}/forest-${815297 + index * 30}_1280.jpg` },
      { id: `multi-media-${index + 1}-4`, url: `https://cdn.pixabay.com/photo/201${8 - (index % 3)}/0${(index % 9) + 4}/0${(index % 9) + 2}/mountains-${276014 + index * 40}_1280.jpg`, type: 'image' as const },
    ],
  })),

  // Shop posts (3)
  ...Array(3).fill(null).map((_, index) => ({
    id: `shop-post-${index + 1}`,
    user_id: `shop-user-${index + 1}`,
    content_id: `shop-content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/photo/2016/11/22/19/08/hangers-1850082_1280.jpg`,
    caption: `New products just arrived in our shop! Check them out now 🛍️ #shopping #newcollection #fashion ${index + 1}`,
    likes: Math.floor(Math.random() * 500) + 100,
    views: Math.floor(Math.random() * 7000) + 800,
    type: 'shop' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 12 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 12 * (index + 1)).toISOString(),
    location: `Shop ${index + 1}, Mall City`,
    users: {
      id: `shop_user_${index + 1}_author`,
      username: `fashion_store_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'women' : 'men'}/${40 + (index % 10)}.jpg`,
      is_verified: true,
      verified: true,
      location: `Shop ${index + 1}, Mall City`,
      is_followed: index % 2 === 0,
    },
    comments: [],
    mediaItems: [
      { id: `shop-${index + 1}-1`, url: `https://cdn.pixabay.com/photo/201${6 - (index % 2)}/1${(index % 9) + 1}/2${(index % 9) + 2}/fashion-${1850082 + index * 100}_1280.jpg`, type: 'image' as const },
      { id: `shop-${index + 1}-2`, url: `https://cdn.pixabay.com/photo/201${7 - (index % 2)}/0${(index % 9) + 2}/1${(index % 9) + 1}/clothing-${1850082 + index * 200}_1280.jpg`, type: 'image' as const },
      { id: `shop-${index + 1}-3`, url: `https://cdn.pixabay.com/photo/201${8 - (index % 2)}/0${(index % 9) + 3}/0${(index % 9) + 3}/accessories-${1850082 + index * 300}_1280.jpg`, type: 'image' as const },
    ],
  })),

  // Poll posts (2)
  ...Array(2).fill(null).map((_, index) => ({
    id: `poll-post-${index + 1}`,
    user_id: `poll-user-${index + 1}`,
    content_id: `poll-content-${index + 1}`,
    media_url: `https://cdn.pixabay.com/photo/2018/01/17/07/06/question-3087623_1280.jpg`,
    caption: `What's your favorite season? Vote now! #poll #question #seasons ${index + 1}`,
    likes: Math.floor(Math.random() * 300) + 50,
    views: Math.floor(Math.random() * 5000) + 500,
    type: 'poll' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 6 * (index + 1)).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 6 * (index + 1)).toISOString(),
    location: index === 0 ? `Poll Center, Opinion City` : undefined,
    users: {
      id: `poll_user_${index + 1}_author`,
      username: `poll_creator_${index + 1}`,
      avatar_url: `https://randomuser.me/api/portraits/${index % 2 === 0 ? 'men' : 'women'}/${50 + (index % 10)}.jpg`,
      is_verified: index === 0,
      verified: index === 0,
      location: index === 0 ? `Poll Center, Opinion City` : undefined,
      is_followed: index === 1,
    },
    comments: [],
    mediaItems: [
      { id: `poll-${index + 1}`, url: `https://cdn.pixabay.com/photo/201${8 - index}/0${1 + index}/1${7 - index}/question-${3087623 + index * 1000}_1280.jpg`, type: 'image' as const }
    ],
    pollOptions: [
      { id: `poll-${index + 1}-option-1`, text: 'Spring', votes: Math.floor(Math.random() * 100) + 10 },
      { id: `poll-${index + 1}-option-2`, text: 'Summer', votes: Math.floor(Math.random() * 100) + 10 },
      { id: `poll-${index + 1}-option-3`, text: 'Fall', votes: Math.floor(Math.random() * 100) + 10 },
      { id: `poll-${index + 1}-option-4`, text: 'Winter', votes: Math.floor(Math.random() * 100) + 10 },
    ],
  })),
];

// Export the demo posts
export default demoPosts;

// Demo story data for StoryFeed component
export const demoStories = [
  // User 1 - creativeminds (multiple stories)
  {
    id: 'story-1-1',
    user: {
      id: 'user-creativeminds',
      username: 'creativeminds',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_1280.jpg',
    mediaType: 'image' as const,
    description: 'Exploring the wonders of the night sky. The stars were absolutely breathtaking last night!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-1-2',
    user: {
      id: 'user-creativeminds',
      username: 'creativeminds',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/02/08/17/24/fantasy-2049567_1280.jpg',
    mediaType: 'image' as const,
    description: 'Fantasy landscapes inspire my creativity. What worlds do you see in this image?',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
    duration: 5,
    viewed: false
  },

  // User 2 - travelbug (multiple stories with video)
  {
    id: 'story-2-1',
    user: {
      id: 'user-travelbug',
      username: 'travelbug',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/13/25/attractive-1869761_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/01/14/13/59/castelmezzano-1979546_1280.jpg',
    mediaType: 'image' as const,
    description: 'Visited this charming village in Italy. The views were absolutely spectacular!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-2-2',
    user: {
      id: 'user-travelbug',
      username: 'travelbug',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/13/25/attractive-1869761_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/video/2019/05/02/23/54/sea-4175298_640.mp4',
    mediaType: 'video' as const,
    videoThumbnail: 'https://cdn.pixabay.com/photo/2019/05/04/15/24/woman-4178302_1280.jpg',
    description: 'The sound of waves is so relaxing. Captured this moment at sunset on the beach.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2.5).toISOString(),
    duration: 15,
    viewed: false
  },
  {
    id: 'story-2-3',
    user: {
      id: 'user-travelbug',
      username: 'travelbug',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/13/25/attractive-1869761_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2016/11/13/12/52/kuala-lumpur-1820944_1280.jpg',
    mediaType: 'image' as const,
    description: 'City lights and urban adventures. Kuala Lumpur has such an amazing skyline!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    duration: 5,
    viewed: false
  },

  // User 3 - musicproducer (single video story)
  {
    id: 'story-3-1',
    user: {
      id: 'user-musicproducer',
      username: 'musicproducer',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/06/46/adult-1867889_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/video/2020/10/17/19/16/concert-5662028_640.mp4',
    mediaType: 'video' as const,
    videoThumbnail: 'https://cdn.pixabay.com/photo/2015/05/15/14/50/concert-768722_1280.jpg',
    description: 'Last night\'s show was incredible! The energy from the crowd was amazing.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    duration: 15,
    viewed: false
  },

  // User 4 - techguru (multiple stories with video)
  {
    id: 'story-4-1',
    user: {
      id: 'user-techguru',
      username: 'techguru',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2016/11/19/14/00/code-1839877_1280.jpg',
    mediaType: 'image' as const,
    description: 'Working on a new project. Coding late into the night but making great progress!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-4-2',
    user: {
      id: 'user-techguru',
      username: 'techguru',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/05/10/19/29/robot-2301646_1280.jpg',
    mediaType: 'image' as const,
    description: 'AI and robotics are the future. What do you think about the rapid advancements in this field?',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4.5).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-4-3',
    user: {
      id: 'user-techguru',
      username: 'techguru',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/video/2021/04/14/14/03/programming-33702_640.mp4',
    mediaType: 'video' as const,
    videoThumbnail: 'https://cdn.pixabay.com/photo/2016/11/30/20/58/programming-1873854_1280.jpg',
    description: 'A day in the life of a programmer. Coffee and code - the perfect combination!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    duration: 15,
    viewed: false
  },

  // User 5 - foodiechef (multiple stories with video)
  {
    id: 'story-5-1',
    user: {
      id: 'user-foodiechef',
      username: 'foodiechef',
      avatar_url: 'https://cdn.pixabay.com/photo/2017/08/01/08/29/woman-2563491_1280.jpg',
      verified: false
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/07/16/10/43/recipe-2508859_1280.jpg',
    mediaType: 'image' as const,
    description: 'Made this delicious pasta dish today. The secret is in the fresh herbs!',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-5-2',
    user: {
      id: 'user-foodiechef',
      username: 'foodiechef',
      avatar_url: 'https://cdn.pixabay.com/photo/2017/08/01/08/29/woman-2563491_1280.jpg',
      verified: false
    },
    mediaUrl: 'https://cdn.pixabay.com/video/2022/05/23/08/38/cooking-112745_640.mp4',
    mediaType: 'video' as const,
    videoThumbnail: 'https://cdn.pixabay.com/photo/2016/11/18/14/05/brick-wall-1834784_1280.jpg',
    description: 'Quick cooking tutorial! This 5-minute recipe is perfect for busy weeknights.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5.5).toISOString(),
    duration: 15,
    viewed: false
  },

  // User 6 - naturephotographer (single story)
  {
    id: 'story-6-1',
    user: {
      id: 'user-naturephotographer',
      username: 'naturephotographer',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg',
      verified: false
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2015/12/01/20/28/road-1072823_1280.jpg',
    mediaType: 'image' as const,
    description: 'Sometimes the journey is more beautiful than the destination. This road leads to endless possibilities.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 7).toISOString(),
    duration: 5,
    viewed: false
  },

  // User 7 - fitnesscoach (multiple stories)
  {
    id: 'story-7-1',
    user: {
      id: 'user-fitnesscoach',
      username: 'fitnesscoach',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/20/22/girl-1871104_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/photo/2017/08/07/14/02/people-2604149_1280.jpg',
    mediaType: 'image' as const,
    description: 'Morning workout session complete! Remember, consistency is key to achieving your fitness goals.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    duration: 5,
    viewed: false
  },
  {
    id: 'story-7-2',
    user: {
      id: 'user-fitnesscoach',
      username: 'fitnesscoach',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/20/22/girl-1871104_1280.jpg',
      verified: true
    },
    mediaUrl: 'https://cdn.pixabay.com/video/2019/06/26/24077-346043893_large.mp4',
    mediaType: 'video' as const,
    videoThumbnail: 'https://cdn.pixabay.com/photo/2017/08/07/14/02/people-2604149_1280.jpg',
    description: 'Here\'s a quick 5-minute workout you can do anywhere! No equipment needed.',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 7.5).toISOString(),
    duration: 15,
    viewed: false
  }
];

// Current user for stories
export const currentStoryUser = {
  id: 'current-user',
  username: 'currentuser',
  avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/23/38/child-1837375_1280.jpg',
  verified: true
};
